import React from 'react';
import { formatDate, formatDateTime } from 'src/utils';
import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import {
  ChangeHistoryDto,
  DrawingHistoryDto,
  RfiHistoryDto,
  SubmittalHistoryDto,
  TransmittalHistoryDto,
} from 'src/api/FiretrolApiClient';

export type {
  ChangeHistoryDto,
  DrawingHistoryDto,
  RfiHistoryDto,
  SubmittalHistoryDto,
  TransmittalHistoryDto,
};

type WorkflowHistoryDto =
  | RfiHistoryDto
  | DrawingHistoryDto
  | ChangeHistoryDto
  | SubmittalHistoryDto
  | TransmittalHistoryDto;

type HistoryField = {
  field: string;
  label: string;
  updated?: string | null;
  old?: string | null;
};

const hasValueData = (field: HistoryField): boolean =>
  field.updated !== undefined || field.old !== undefined;

const isEmptyValue = (value: string | null | undefined): boolean =>
  value === undefined || value === null || value === '';

export interface IHistoryListProps {
  history: WorkflowHistoryDto[];
}

const parseFields = (fieldsJson: string | undefined): HistoryField[] => {
  if (!fieldsJson) return [];
  try {
    return JSON.parse(fieldsJson) as HistoryField[];
  } catch {
    return [];
  }
};

const renderBadges = (fields: HistoryField[]) => {
  return fields.map((field) => <Badge key={field.field}>{field.label}</Badge>);
};

const renderValue = (value: string | null | undefined, strike: boolean) => {
  const empty = isEmptyValue(value);
  return (
    <Text
      component="span"
      size="sm"
      c={empty || strike ? 'dimmed' : 'dark'}
      td={strike ? 'line-through' : undefined}
      fs={empty ? 'italic' : undefined}
      lineClamp={2}
      style={{ overflowWrap: 'anywhere' }}
    >
      {empty ? '(empty)' : value}
    </Text>
  );
};

const renderValuedRow = (field: HistoryField) => (
  <Group key={field.field} gap="xs" wrap="nowrap" align="flex-start">
    <Text size="sm" fw={600} c="dark" style={{ whiteSpace: 'nowrap' }}>
      {field.label}:
    </Text>
    {renderValue(field.old, true)}
    <Text size="sm" c="dimmed">
      →
    </Text>
    {renderValue(field.updated, false)}
  </Group>
);

/**
 * @registry Read-only workflow history feed shared across workflow detail pages. Renders each history entry (who/when/action + updated fields with old→new values) from the entity's `history` array.
 */
export const HistoryList: React.FC<IHistoryListProps> = ({ history }) => {
  const renderItems = () => {
    return history.map((hs) => {
      const fields = parseFields(hs.fields);
      const badgeFields = fields.filter((f) => !hasValueData(f));
      const valuedFields = fields.filter(hasValueData);
      return (
        <Paper key={hs.id} mb="sm">
          <Stack gap="xs">
            <Text size="sm" c="dark">
              {formatDate(hs.createdAt, 'MMM Do YYYY')} at{' '}
              {formatDateTime(hs.createdAt, 'h:mma')}: {hs.person}{' '}
              {hs.statusChange ? 'changed status to ' : ' '}
              <b>{hs.action?.toUpperCase()}</b> {hs.resourceTitle}.
            </Text>
            {hs.statusChange && hs.previousStatus && hs.updatedStatus && (
              <Text size="xs" c="dimmed">
                {hs.previousStatus} → {hs.updatedStatus}
              </Text>
            )}
            {fields.length > 0 && (
              <Stack gap={4}>
                <Text size="sm" fw={700} c="dark">
                  Updated Fields:
                </Text>
                {badgeFields.length > 0 && (
                  <Group gap="xs">{renderBadges(badgeFields)}</Group>
                )}
                {valuedFields.map(renderValuedRow)}
              </Stack>
            )}
          </Stack>
        </Paper>
      );
    });
  };
  return <Stack mt="md">{renderItems()}</Stack>;
};
